import { useEffect, useRef, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { FilePdf } from "@phosphor-icons/react";
import PdfHeaderPreview from "./PdfHeaderPreview";
import { toast } from "sonner";

const EMPTY = { company_name: "", tagline: "", logo_url: "", footer_text: "", primary_color: "", logo_scale: 1, hide_name: false };

/** Same per-client white-label branding flow as the client quote PDFs: load saved client branding, override per download. */
export default function PdfBrandingDialog({ open, onOpenChange, clientId, onDownload, title = "PDF Branding", description, hint, documentLabel, hseLetterhead = true }) {
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [hseSaved, setHseSaved] = useState(null);
  const [effective, setEffective] = useState(null);
  const [saveDefault, setSaveDefault] = useState(false);
  const [saveClient, setSaveClient] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY); setSaveDefault(false); setSaveClient(false);
    API.get("/hse/branding").then(({ data }) => {
      const eff = hseLetterhead ? data.effective : data.tenant;  // non-H&S PDFs sit on the plain company brand
      setHseSaved(hseLetterhead ? (data.saved || {}) : {}); setEffective(eff || null);
      // the two layout switches start from what the PDF does today, so the preview matches before anything is typed
      setForm(f => ({ ...f, logo_scale: Number(eff?.logo_scale) === 1.5 ? 1.5 : 1, hide_name: !!eff?.hide_name }));
    }).catch(() => setHseSaved({}));
    if (!clientId) { setSaved(null); return; }
    API.get(`/clients/${clientId}/branding`).then(({ data }) => setSaved(data)).catch(() => setSaved(null));
  }, [open, clientId, hseLetterhead]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/upload?entity_type=branding&entity_id=logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, logo_url: data.file_url || `/api/files/${data.file_id}/${data.file_name}` }));
    } catch { toast.error("Logo upload failed"); } finally { setUploading(false); }
  };

  const run = async (branded) => {
    setBusy(true);
    try {
      if (branded && saveDefault) {
        const { data } = await API.put("/hse/branding", form);
        setHseSaved(data.saved || {}); setPreviewKey(k => k + 1);
        toast.success("Saved as your HSE letterhead — every H&S PDF will use it from now on");
      }
      await onDownload(branded ? { ...form, save_for_client: !!clientId && saveClient } : null); onOpenChange(false);
    }
    catch (err) { toast.error(err?.response?.data?.detail ? formatApiError(err) : "Failed to generate PDF"); } finally { setBusy(false); }
  };
  const clearHseSaved = async () => {
    try { await API.put("/hse/branding", {}); setHseSaved({}); setPreviewKey(k => k + 1); toast.success("Saved HSE letterhead cleared — back to your company brand"); }
    catch { toast.error("Couldn't clear the saved letterhead"); }
  };

  const hasSaved = saved && (saved.company_name || saved.logo_url || saved.footer_text);
  const hasHseSaved = hseSaved && Object.keys(hseSaved).length > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="pdf-branding-dialog">
        <DialogHeader><DialogTitle className="font-[Chivo]">{title}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{description || "Brand this document for the client. Leave blank to use your default company branding."}</p>
        {hasSaved && (
          <Button variant="outline" size="sm" className="w-full" data-testid="pdf-branding-load-saved"
            onClick={() => setForm(f => ({ ...f, company_name: saved.company_name || "", tagline: saved.tagline || "", logo_url: saved.logo_url || "", footer_text: saved.footer_text || "", primary_color: saved.primary_color || "" }))}>
            Load saved client branding{saved.company_name ? ` (${saved.company_name})` : ""}
          </Button>
        )}
        <PdfHeaderPreview form={form} savedClient={hasSaved} clientSaved={hasSaved ? saved : null} refreshKey={previewKey} effective={effective} includeHse={hseLetterhead} documentLabel={documentLabel || title.replace(/ (PDF )?branding$/i, "").toUpperCase() || "DOCUMENT"} />
        {hasHseSaved && (
          <div className="flex items-center gap-2" data-testid="pdf-branding-hse-saved">
            <Button variant="outline" size="sm" className="flex-1" data-testid="pdf-branding-load-hse"
              onClick={() => setForm({ company_name: hseSaved.company_name || "", tagline: hseSaved.tagline || "", logo_url: hseSaved.logo_url || "", footer_text: hseSaved.footer_text || "", primary_color: hseSaved.primary_color || "", logo_scale: Number(hseSaved.logo_scale) === 1.5 ? 1.5 : 1, hide_name: !!hseSaved.hide_name })}>
              Edit saved HSE letterhead{hseSaved.company_name ? ` (${hseSaved.company_name})` : ""}
            </Button>
            <Button variant="ghost" size="sm" className="text-red-600" onClick={clearHseSaved} data-testid="pdf-branding-clear-hse">Clear</Button>
          </div>
        )}
        <div>
          <Label className="text-xs font-semibold">Logo</Label>
          <div className="border-2 border-dashed border-border rounded-lg p-3 text-center cursor-pointer hover:border-primary/50 mt-1" onClick={() => fileRef.current?.click()} data-testid="pdf-branding-logo-drop">
            {form.logo_url ? (
              <div className="flex items-center justify-center gap-3">
                <img src={form.logo_url.startsWith("http") ? form.logo_url : `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}`} alt="Logo" className="h-10 max-w-[120px] object-contain" />
                <button type="button" onClick={(e) => { e.stopPropagation(); setForm(f => ({ ...f, logo_url: "" })); }} className="text-xs text-red-500 hover:underline">Remove</button>
              </div>
            ) : <p className="text-xs text-muted-foreground">{uploading ? "Uploading…" : "Click to upload a logo (PNG/JPG)"}</p>}
          </div>
          <input ref={fileRef} type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={e => e.target.files?.[0] && upload(e.target.files[0])} />
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-xs">
            <label className="flex items-center gap-2">
              <span className="text-muted-foreground">Logo size</span>
              <select value={String(form.logo_scale)} onChange={e => setForm(f => ({ ...f, logo_scale: Number(e.target.value) }))} className="h-7 rounded-md border border-input bg-background px-2 text-xs" data-testid="pdf-branding-logo-scale">
                <option value="1">Standard</option>
                <option value="1.5">Large (×1.5)</option>
              </select>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!form.hide_name} onChange={e => setForm(f => ({ ...f, hide_name: !e.target.checked }))} className="h-3.5 w-3.5 accent-primary" data-testid="pdf-branding-show-name" />
              <span>Show company name under the logo</span>
            </label>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2"><Label className="text-xs font-semibold">Company name</Label>
            <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} className="mt-1" data-testid="pdf-branding-company" /></div>
          <div><Label className="text-xs font-semibold">Accent colour</Label>
            <Input value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))} placeholder="#1D4ED8" className="mt-1" data-testid="pdf-branding-colour" /></div>
        </div>
        <div><Label className="text-xs font-semibold">Tagline</Label>
          <Input value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} className="mt-1" data-testid="pdf-branding-tagline" /></div>
        <div><Label className="text-xs font-semibold">Footer text</Label>
          <Textarea rows={2} value={form.footer_text} onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))} className="mt-1 text-xs" data-testid="pdf-branding-footer" /></div>
        {hseLetterhead && (
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="pdf-branding-save-default-label">
            <input type="checkbox" checked={saveDefault} onChange={e => setSaveDefault(e.target.checked)} className="h-3.5 w-3.5 accent-primary" data-testid="pdf-branding-save-default" />
            <span>Save as my HSE letterhead so every H&amp;S PDF uses it from now on</span>
          </label>
        )}
        {clientId && (
          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="pdf-branding-save-client-label">
            <input type="checkbox" checked={saveClient} onChange={e => setSaveClient(e.target.checked)} className="h-3.5 w-3.5 accent-primary" data-testid="pdf-branding-save-client" />
            <span>Save as this client's branding (also used on their portal and emails)</span>
          </label>
        )}
        <p className="text-[10px] text-muted-foreground">{hint || (clientId ? "Applies to this download only unless you save it for the client." : "Link a job with a client to save branding for reuse.")}</p>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => run(false)} disabled={busy} data-testid="pdf-branding-default">Use default</Button>
          <Button size="sm" onClick={() => run(true)} disabled={busy} data-testid="pdf-branding-download"><FilePdf size={14} className="mr-1" /> {busy ? "Generating…" : "Download branded PDF"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function downloadBlob(data, filename) {
  const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}
